/**
 * Seed data for mock mode: "Summit Rotor Sales", a helicopter dealer in Colorado, two weeks into
 * the Launch Partner program. Dates are relative to page load so the demo always looks current.
 */
import type * as T from '../types';

// ── helpers ──────────────────────────────────────────────────────────────────
export function mkId(n: number, prefix = 'a'): string {
  const hex = n.toString(16).padStart(12, '0');
  const p = prefix.charCodeAt(0).toString(16).padStart(2, '0');
  return `${p}000000-0000-4000-8000-${hex}`;
}
const NOW = Date.now();
const DAY = 86_400_000;
export const daysAgo = (d: number, h = 0): string => new Date(NOW - d * DAY - h * 3_600_000).toISOString();
export const daysAhead = (d: number): string => new Date(NOW + d * DAY).toISOString();
export const dayStr = (d: number): string => new Date(NOW - d * DAY).toISOString().slice(0, 10);

/** Deterministic PRNG so the demo is stable across reloads. */
export function prng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/** Inline SVG placeholder "photo" — no network, no console noise. */
export function photo(seed: number, label: string): string {
  const hues = [205, 28, 190, 150, 260, 340, 45, 220];
  const h = hues[seed % hues.length];
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 427'>
<defs><linearGradient id='s' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='hsl(${h},55%,70%)'/><stop offset='1' stop-color='hsl(${h + 18},50%,90%)'/></linearGradient></defs>
<rect width='640' height='427' fill='url(#s)'/>
<path d='M0 340 L140 300 L260 325 L400 285 L520 318 L640 296 L640 427 L0 427Z' fill='hsl(${h},20%,42%)' opacity='.55'/>
<rect y='350' width='640' height='77' fill='hsl(${h},14%,30%)'/>
<g transform='translate(${180 + (seed % 5) * 14},170)' fill='hsl(${h},25%,16%)'>
<rect x='-150' y='-6' width='420' height='5' rx='2'/>
<rect x='56' y='-6' width='8' height='26' />
<ellipse cx='60' cy='60' rx='78' ry='42'/>
<path d='M120 52 L300 44 L300 58 L120 74Z'/>
<rect x='292' y='18' width='10' height='46' rx='3'/>
<ellipse cx='18' cy='52' rx='26' ry='20' fill='hsl(${h},45%,78%)' opacity='.7'/>
<rect x='5' y='112' width='120' height='5' rx='2'/><rect x='30' y='98' width='5' height='16'/><rect x='95' y='98' width='5' height='16'/>
</g>
<text x='24' y='404' font-family='-apple-system,Helvetica,Arial' font-size='22' font-weight='600' fill='white' opacity='.92'>${label}</text>
</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function slugify(title: string, id: string): string {
  const s = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return s ? `${s.slice(0, 60)}-${id.slice(0, 8)}` : id;
}

// ── people ───────────────────────────────────────────────────────────────────
export const U_ME = mkId(1, 'u');
export const U_PRIYA = mkId(2, 'u');
export const U_MARCO = mkId(3, 'u');
export const U_LEE = mkId(4, 'u');
export const U_RT = mkId(5, 'u');

export const USERS: Record<string, { name: string; email: string }> = {
  [U_ME]: { name: 'Dana Whitaker', email: 'dana@summitrotor.com' },
  [U_PRIYA]: { name: 'Priya Natarajan', email: 'priya@summitrotor.com' },
  [U_MARCO]: { name: 'Marco Ruiz', email: 'marco@summitrotor.com' },
  [U_LEE]: { name: 'Lee Chen', email: 'lee@summitrotor.com' },
  [U_RT]: { name: 'Owen Hart', email: 'owen@rotortrade.com' },
};

export const ME: T.Me = { id: U_ME, email: USERS[U_ME].email, display_name: USERS[U_ME].name, is_staff: true };

// ── makes / models ───────────────────────────────────────────────────────────
export const MAKES: T.Make[] = [
  { id: 1, name: 'Airbus Helicopters', slug: 'airbus-helicopters' },
  { id: 2, name: 'Bell', slug: 'bell' },
  { id: 3, name: 'Robinson', slug: 'robinson' },
  { id: 4, name: 'MD Helicopters', slug: 'md-helicopters' },
  { id: 5, name: 'Leonardo', slug: 'leonardo' },
  { id: 6, name: 'Sikorsky', slug: 'sikorsky' },
  { id: 7, name: 'Enstrom', slug: 'enstrom' },
  { id: 8, name: 'Schweizer', slug: 'schweizer' },
  { id: 20, name: 'Cessna', slug: 'cessna' },
  { id: 21, name: 'Piper', slug: 'piper' },
  { id: 22, name: 'Beechcraft', slug: 'beechcraft' },
  { id: 23, name: 'Pilatus', slug: 'pilatus' },
  { id: 30, name: 'AutoGyro', slug: 'autogyro' },
];
const H = 'helicopter' as const;
export const MODELS: T.Model[] = [
  { id: 101, make_id: 1, name: 'AS350 B2', slug: 'as350-b2', aircraft_class: H, engine_count: 1 },
  { id: 102, make_id: 1, name: 'AS350 B3e', slug: 'as350-b3e', aircraft_class: H, engine_count: 1 },
  { id: 103, make_id: 1, name: 'H125', slug: 'h125', aircraft_class: H, engine_count: 1 },
  { id: 104, make_id: 1, name: 'H130 (EC130 T2)', slug: 'h130', aircraft_class: H, engine_count: 1 },
  { id: 105, make_id: 1, name: 'H135 (EC135)', slug: 'h135', aircraft_class: H, engine_count: 2 },
  { id: 106, make_id: 1, name: 'AS355 NP', slug: 'as355-np', aircraft_class: H, engine_count: 2 },
  { id: 201, make_id: 2, name: '206B3 JetRanger', slug: '206b3', aircraft_class: H, engine_count: 1 },
  { id: 202, make_id: 2, name: '206L4 LongRanger', slug: '206l4', aircraft_class: H, engine_count: 1 },
  { id: 203, make_id: 2, name: '407', slug: '407', aircraft_class: H, engine_count: 1 },
  { id: 204, make_id: 2, name: '407GXi', slug: '407gxi', aircraft_class: H, engine_count: 1 },
  { id: 205, make_id: 2, name: '505 Jet Ranger X', slug: '505', aircraft_class: H, engine_count: 1 },
  { id: 206, make_id: 2, name: '412EP', slug: '412ep', aircraft_class: H, engine_count: 2 },
  { id: 301, make_id: 3, name: 'R22 Beta II', slug: 'r22', aircraft_class: H, engine_count: 1 },
  { id: 302, make_id: 3, name: 'R44 Raven II', slug: 'r44-raven-ii', aircraft_class: H, engine_count: 1 },
  { id: 303, make_id: 3, name: 'R44 Clipper II', slug: 'r44-clipper-ii', aircraft_class: H, engine_count: 1 },
  { id: 304, make_id: 3, name: 'R66 Turbine', slug: 'r66', aircraft_class: H, engine_count: 1 },
  { id: 401, make_id: 4, name: '500E', slug: '500e', aircraft_class: H, engine_count: 1 },
  { id: 402, make_id: 4, name: '530F', slug: '530f', aircraft_class: H, engine_count: 1 },
  { id: 501, make_id: 5, name: 'AW119 Kx', slug: 'aw119', aircraft_class: H, engine_count: 1 },
  { id: 502, make_id: 5, name: 'AW109 SP', slug: 'aw109sp', aircraft_class: H, engine_count: 2 },
  { id: 601, make_id: 6, name: 'S-76C++', slug: 's-76', aircraft_class: H, engine_count: 2 },
  { id: 701, make_id: 7, name: '480B', slug: '480b', aircraft_class: H, engine_count: 1 },
  { id: 801, make_id: 8, name: '300CBi', slug: '300cbi', aircraft_class: H, engine_count: 1 },
  { id: 2001, make_id: 20, name: '172S Skyhawk', slug: '172s', aircraft_class: 'airplane', engine_count: 1 },
  { id: 2002, make_id: 20, name: '208B Grand Caravan', slug: '208b', aircraft_class: 'airplane', engine_count: 1 },
  { id: 2101, make_id: 21, name: 'PA-28-181 Archer', slug: 'pa-28-181', aircraft_class: 'airplane', engine_count: 1 },
  { id: 2201, make_id: 22, name: 'King Air 350', slug: 'king-air-350', aircraft_class: 'airplane', engine_count: 2 },
  { id: 2301, make_id: 23, name: 'PC-12 NGX', slug: 'pc-12', aircraft_class: 'airplane', engine_count: 1 },
  { id: 3001, make_id: 30, name: 'Cavalon', slug: 'cavalon', aircraft_class: 'gyroplane', engine_count: 1 },
];

// ── accounts ─────────────────────────────────────────────────────────────────
export const A_SUMMIT = mkId(1, 'c');
export const A_ME = mkId(2, 'c');
export const A_RT = mkId(3, 'c');
export const A_PACIFIC = mkId(4, 'c');
export const A_HIGHDESERT = mkId(5, 'c');
export const A_TORRES = mkId(6, 'c');
export const A_KIM = mkId(7, 'c');

function account(p: Partial<T.Account> & Pick<T.Account, 'id' | 'kind' | 'display_name' | 'slug'>): T.Account {
  return {
    user_id: null, company_id: null, status: 'active', trust_tier: 0, daily_spend_cap_cents: 5000,
    stripe_customer_id: null, default_payment_method_id: null, card_brand: null, card_last4: null, card_ok: false,
    auto_recharge_enabled: true, auto_recharge_threshold_cents: 2000, auto_recharge_amount_cents: 10000,
    identity_verified: false, domain_verified: false, verified: false, storefront: {},
    response_time_minutes_p50: null, created_at: daysAgo(60), updated_at: daysAgo(1),
    ...p,
  };
}

export const ACCOUNTS: T.Account[] = [
  account({
    id: A_SUMMIT, kind: 'dealer', company_id: mkId(1, 'm'), display_name: 'Summit Rotor Sales', slug: 'summit-rotor-sales',
    trust_tier: 1, daily_spend_cap_cents: 25000, stripe_customer_id: 'cus_mockSummit', default_payment_method_id: 'pm_mock_visa',
    card_brand: 'visa', card_last4: '4242', card_ok: true, auto_recharge_enabled: true,
    auto_recharge_threshold_cents: 5000, auto_recharge_amount_cents: 25000,
    identity_verified: false, domain_verified: true, verified: true, response_time_minutes_p50: 38,
    created_at: daysAgo(62),
    storefront: {
      logo_url: null, banner_url: null,
      about: 'Summit Rotor Sales has bought, sold and brokered turbine and piston helicopters out of the Front Range since 2009. Every aircraft we list has been through our own pre-buy and logbook review — we tell you what we found.',
      phone: '+1 303-555-0142', email: 'sales@summitrotor.com', website: 'https://summitrotor.com',
      locations: [
        { name: 'Main hangar', ident: 'KBJC', city: 'Broomfield', state: 'CO', country: 'US' },
        { name: 'Western Slope', ident: 'KGJT', city: 'Grand Junction', state: 'CO', country: 'US' },
      ],
    },
  }),
  account({
    id: A_ME, kind: 'individual', user_id: U_ME, display_name: 'Dana Whitaker', slug: 'dana-whitaker',
    card_brand: 'visa', card_last4: '4242', card_ok: true, created_at: daysAgo(90),
  }),
  account({
    id: A_RT, kind: 'dealer', company_id: mkId(2, 'm'), display_name: 'RotorTrade', slug: 'rotortrade', trust_tier: 2,
    daily_spend_cap_cents: 100000, card_brand: 'amex', card_last4: '1005', card_ok: true, identity_verified: true, verified: true,
    created_at: daysAgo(45),
  }),
  account({
    id: A_PACIFIC, kind: 'dealer', company_id: mkId(3, 'm'), display_name: 'Pacific Heli Brokers', slug: 'pacific-heli-brokers',
    status: 'suspended', card_brand: 'mastercard', card_last4: '4444', card_ok: true, created_at: daysAgo(30),
  }),
  account({
    id: A_HIGHDESERT, kind: 'dealer', company_id: mkId(4, 'm'), display_name: 'High Desert Aviation', slug: 'high-desert-aviation',
    trust_tier: 1, daily_spend_cap_cents: 25000, card_brand: 'visa', card_last4: '1881', card_ok: true, domain_verified: true, verified: true,
    created_at: daysAgo(20),
  }),
  account({ id: A_TORRES, kind: 'individual', user_id: mkId(10, 'u'), display_name: 'Mike Torres', slug: 'mike-torres', card_brand: 'visa', card_last4: '0077', card_ok: true, created_at: daysAgo(12) }),
  account({ id: A_KIM, kind: 'individual', user_id: mkId(11, 'u'), display_name: 'Sarah Kim', slug: 'sarah-kim', created_at: daysAgo(3) }),
];

export const MY_ROLES: Record<string, T.MemberRole> = { [A_SUMMIT]: 'owner', [A_ME]: 'owner' };

export const MEMBERS: T.Member[] = [
  { account_id: A_SUMMIT, user_id: U_ME, role: 'owner', status: 'active', invited_email: null, display_name: USERS[U_ME].name, email: USERS[U_ME].email, created_at: daysAgo(62) },
  { account_id: A_SUMMIT, user_id: U_PRIYA, role: 'admin', status: 'active', invited_email: null, display_name: USERS[U_PRIYA].name, email: USERS[U_PRIYA].email, created_at: daysAgo(60) },
  { account_id: A_SUMMIT, user_id: U_MARCO, role: 'sales', status: 'active', invited_email: null, display_name: USERS[U_MARCO].name, email: USERS[U_MARCO].email, created_at: daysAgo(55) },
  { account_id: A_SUMMIT, user_id: U_LEE, role: 'billing', status: 'active', invited_email: null, display_name: USERS[U_LEE].name, email: USERS[U_LEE].email, created_at: daysAgo(40) },
  { account_id: A_SUMMIT, user_id: null, role: 'sales', status: 'invited', invited_email: 'sam@summitrotor.com', display_name: null, email: 'sam@summitrotor.com', created_at: daysAgo(2) },
  { account_id: A_ME, user_id: U_ME, role: 'owner', status: 'active', invited_email: null, display_name: USERS[U_ME].name, email: USERS[U_ME].email, created_at: daysAgo(90) },
];

// ── listings ─────────────────────────────────────────────────────────────────
type LSeed = Partial<T.MyListing> & Pick<T.MyListing, 'title' | 'kind' | 'state' | 'review_status'>;

function listing(n: number, p: LSeed): T.MyListing {
  const id = mkId(n, 'l');
  const make = MAKES.find((m) => m.id === p.make_id)?.name ?? null;
  const model = MODELS.find((m) => m.id === p.model_id)?.name ?? null;
  return {
    id, account_id: A_SUMMIT, description: null, make_id: null, model_id: null, make, model, year: null,
    serial_number: null, registration: null, registry_match: 'unchecked', price_cents: null, price_on_request: false,
    currency: 'USD', condition: 'used', location_ident: 'KBJC', location_state: 'CO', location_country: 'US',
    images: [], attributes: {}, custom_attributes: [], review_reasons: [], reviewed_at: daysAgo(10),
    quality_score: 70, billing_basis: 'paid', published_at: daysAgo(30), expires_at: null, sold_at: null,
    source: 'manual', external_id: null, slug: slugify(p.title, id), created_at: daysAgo(35), updated_at: daysAgo(2),
    stats: { views: 0, saves: 0, leads: 0, spend_cents: 0 },
    ...p,
  };
}

const eng = (make: string, model: string, serial: string, tsn: number, tso: number | null, tbo: number, hs: number | null) => ({
  position: 'single', make, model, serial, tsn_hours: tsn, tso_hours: tso, tsn_cycles: Math.round(tsn * 1.9), tbo_hours: tbo,
  hot_section_hours_since: hs, overhaul_shop: tso != null ? 'Rolls-Royce / Safran authorised' : null,
});

export const LISTINGS: T.MyListing[] = [
  listing(1, {
    title: '2016 Airbus AS350 B3e — 1,840 hrs, VIP interior', kind: 'helicopter', state: 'active', review_status: 'approved',
    make_id: 1, model_id: 102, year: 2016, serial_number: '8142', registration: 'N350SR', registry_match: 'matched',
    price_cents: 285_000_000, quality_score: 94, images: [1, 2, 3, 4, 5].map((i) => photo(i, 'AS350 B3e · N350SR')),
    description: 'One-owner corporate B3e, hangared since new. Arriel 2D on Safran SBH. Garmin G500H TXi, GTN 750Xi, HTAWS, ADS-B in/out. 6-place VIP leather, air conditioning, dual controls. No damage history, complete logs.',
    attributes: {
      configuration: 'corporate/VIP', seats: 6, ifr_certified: false, export_ready: true, registration_country: 'US', location_airport: 'KBJC',
      airframe_total_hours: 1840, airframe_total_cycles: 3120, landings: 4410, useful_load_lbs: 2350, max_gross_weight_lbs: 4961,
      cargo_hook: true, float_kit: false, wire_strike_kit: true,
      engines: [eng('Safran', 'Arriel 2D', '50412', 1840, null, 4000, 920)], engine_program: 'other',
      main_rotor_blades_hours_remaining: 18160, tail_rotor_blades_hours_remaining: 18160,
      life_limited_parts: [
        { part: 'MR mast', serial: 'M-2231', hours_remaining: 18160, cycles_remaining: null, calendar_due: null },
        { part: 'Starflex star', serial: 'SF-1189', hours_remaining: 2160, cycles_remaining: null, calendar_due: '2029-05-01' },
      ],
      avionics: ['Garmin G500H TXi', 'Garmin GTN 750Xi', 'HTAWS', 'ADS-B In/Out', 'Bose A20 x6'], autopilot: 'None (SAS)', night_vision: false,
      interior_condition: '9', interior_year: 2016, exterior_condition: '9', paint_year: 2016,
      last_annual_date: daysAgo(70).slice(0, 10), next_inspection_due: '600 hr / 12 mo — Mar 2027',
      damage_history: false, logs_complete: true,
    },
    custom_attributes: [
      { label: 'Air conditioning', value: 'Air Comm vapor cycle', mapped_key: null, confidence: null },
      { label: 'TTAF since new', value: '1,840', mapped_key: 'airframe_total_hours', confidence: 0.97 },
      { label: 'Hangar kept', value: 'Yes, since new', mapped_key: null, confidence: 0.2 },
    ],
    stats: { views: 2140, saves: 63, leads: 9, spend_cents: 8400, views_7d: 318, leads_7d: 2 },
    published_at: daysAgo(42),
  }),
  listing(2, {
    title: '2012 Bell 407 — utility, cargo hook, 4,210 hrs', kind: 'helicopter', state: 'active', review_status: 'approved',
    make_id: 2, model_id: 203, year: 2012, serial_number: '54122', registration: 'N407SM', registry_match: 'matched',
    price_cents: 195_000_000, quality_score: 88, images: [3, 4, 6].map((i) => photo(i, 'Bell 407 · N407SM')),
    description: 'Utility-configured 407 with cargo hook, long-line mirror, and Dart high skids. RR 250-C47B on PowerAdvantage. Fresh 300-hr. Ready to work.',
    attributes: {
      configuration: 'utility', seats: 7, airframe_total_hours: 4210, landings: 9820, cargo_hook: true, wire_strike_kit: true,
      engines: [eng('Rolls-Royce', '250-C47B', 'CAE-847321', 4210, 1210, 3500, 410)], engine_program: 'PowerAdvantage',
      avionics: ['Garmin GTN 650', 'GTX 345R', 'Garmin G500H'], ifr_certified: false, damage_history: false, logs_complete: true,
      interior_condition: '6', exterior_condition: '7', paint_year: 2019,
    },
    stats: { views: 1480, saves: 41, leads: 6, spend_cents: 8400, views_7d: 201, leads_7d: 1 },
  }),
  listing(3, {
    title: '2018 Robinson R44 Raven II — 680 hrs, A/C', kind: 'helicopter', state: 'active', review_status: 'approved',
    make_id: 3, model_id: 302, year: 2018, serial_number: '14390', registration: 'N44SR', registry_match: 'matched',
    price_cents: 38_900_000, quality_score: 81, images: [2, 7].map((i) => photo(i, 'R44 Raven II')),
    attributes: { seats: 4, airframe_total_hours: 680, configuration: 'training', avionics: ['Garmin GTN 650', 'Garmin G5'], logs_complete: true, damage_history: false },
    stats: { views: 980, saves: 22, leads: 4, spend_cents: 8400, views_7d: 120, leads_7d: 1 },
  }),
  listing(4, {
    title: '2009 Bell 407 — EMS interior removed, fresh paint', kind: 'helicopter', state: 'pending_review', review_status: 'pending',
    make_id: 2, model_id: 203, year: 2009, serial_number: '53901', registration: 'N914SR', price_cents: 149_500_000, quality_score: 72,
    images: [5, 1].map((i) => photo(i, 'Bell 407 · N914SR')), published_at: null, reviewed_at: null, billing_basis: null,
    attributes: { configuration: 'EMS', seats: 6, airframe_total_hours: 9880, paint_year: 2026 },
    stats: { views: 0, saves: 0, leads: 0, spend_cents: 0 }, created_at: daysAgo(1), updated_at: daysAgo(0, 2),
  }),
  listing(5, {
    title: '2014 Robinson R66 Turbine — police package', kind: 'helicopter', state: 'active', review_status: 'limited',
    make_id: 3, model_id: 304, year: 2014, serial_number: '0511', registration: 'N66PD', registry_match: 'mismatch',
    price_cents: 72_500_000, quality_score: 64, images: [6].map((i) => photo(i, 'R66 Turbine')),
    review_reasons: [{ code: 'registry_mismatch', message: 'FAA registry lists N66PD with serial 0515, not 0511. Correct the serial or registration to restore full visibility and promotion.', field: 'serial_number' }],
    attributes: { configuration: 'law enforcement', seats: 5, airframe_total_hours: 3020, night_vision: true },
    stats: { views: 610, saves: 9, leads: 2, spend_cents: 8400, views_7d: 64, leads_7d: 0 },
  }),
  listing(6, {
    title: '2005 MD 500E — Rolls-Royce 250-C20B, 5,100 hrs', kind: 'helicopter', state: 'paused', review_status: 'approved',
    make_id: 4, model_id: 401, year: 2005, serial_number: '0612E', registration: 'N500SV', registry_match: 'matched',
    price_cents: 89_000_000, quality_score: 77, images: [4, 8].map((i) => photo(i, 'MD 500E')),
    attributes: { seats: 5, airframe_total_hours: 5100, configuration: 'utility' },
    stats: { views: 520, saves: 14, leads: 1, spend_cents: 4200, views_7d: 0, leads_7d: 0 },
  }),
  listing(7, {
    title: '2020 Airbus H125 — 950 hrs, G500H TXi, HTAWS', kind: 'helicopter', state: 'active', review_status: 'approved',
    make_id: 1, model_id: 103, year: 2020, serial_number: '9012', registration: 'N125SR', registry_match: 'matched',
    price_cents: 349_000_000, quality_score: 91, images: [1, 7, 3].map((i) => photo(i, 'H125 · N125SR')),
    attributes: { seats: 6, airframe_total_hours: 950, configuration: 'tour', avionics: ['Garmin G500H TXi', 'HTAWS'], cargo_hook: true, logs_complete: true, damage_history: false },
    stats: { views: 1710, saves: 52, leads: 5, spend_cents: 8400, views_7d: 260, leads_7d: 2 },
  }),
  listing(8, {
    title: '2001 Bell 206B3 JetRanger — SOLD', kind: 'helicopter', state: 'sold', review_status: 'approved',
    make_id: 2, model_id: 201, year: 2001, registration: 'N206JR', registry_match: 'matched', price_cents: 74_500_000, quality_score: 80,
    images: [2].map((i) => photo(i, 'Bell 206B3')), sold_at: daysAgo(6),
    attributes: { seats: 5, airframe_total_hours: 11200 },
    stats: { views: 1320, saves: 30, leads: 7, spend_cents: 6400 },
  }),
  listing(9, {
    title: 'Rolls-Royce 250-C20B engine — 1,140 TSO, 8130', kind: 'engine', state: 'active', review_status: 'approved',
    price_cents: 21_500_000, condition: 'overhauled', quality_score: 79, images: [8].map((i) => photo(i, '250-C20B')),
    attributes: { part_number: '23065818', serial_number_part: 'CAE-831225', condition_tag: 'OH', tsn_hours: 8820, tso_hours: 1140, faa_8130: true, trace: 'Part 135 operator', quantity: 1 },
    stats: { views: 260, saves: 6, leads: 1, spend_cents: 2100 },
  }),
  listing(10, {
    title: 'Garmin GTN 650Xi — new in box, with install kit', kind: 'avionics', state: 'active', review_status: 'approved',
    price_cents: 1_249_500, condition: 'new', quality_score: 74, images: [3].map((i) => photo(i, 'GTN 650Xi')),
    attributes: { part_number: '010-02327-02', condition_tag: 'NE', quantity: 2, faa_8130: false },
    stats: { views: 190, saves: 11, leads: 2, spend_cents: 1050 },
  }),
  listing(11, {
    title: '2011 Robinson R44 Clipper II — pop-out floats', kind: 'helicopter', state: 'draft', review_status: 'pending',
    make_id: 3, model_id: 303, year: 2011, price_cents: null, quality_score: 35, images: [], published_at: null, reviewed_at: null, billing_basis: null,
    attributes: { float_kit: true, airframe_total_hours: 1960 },
    stats: { views: 0, saves: 0, leads: 0, spend_cents: 0 }, created_at: daysAgo(4), updated_at: daysAgo(4),
  }),
  listing(12, {
    title: '1998 Airbus AS350 B2 — project, needs engine', kind: 'helicopter', state: 'rejected', review_status: 'rejected',
    make_id: 1, model_id: 101, year: 1998, registration: 'N98BZ', registry_match: 'not_found', price_cents: 28_000_000, condition: 'for_parts', quality_score: 30,
    images: [5].map((i) => photo(i, 'AS350 B2')), published_at: null, billing_basis: null,
    review_reasons: [
      { code: 'photo_reused', message: 'Photo 1 matches a photo already used by a different seller’s listing. Upload your own photos of this aircraft.', field: 'images' },
      { code: 'registry_not_found', message: 'N98BZ is not in the FAA registry. Check the registration, or leave it blank and add the serial number.', field: 'registration' },
    ],
    attributes: { airframe_total_hours: 14200 },
    stats: { views: 0, saves: 0, leads: 0, spend_cents: 0 },
  }),
];

export const DOCUMENTS: T.ListingDocument[] = [
  { id: mkId(1, 'd'), listing_id: LISTINGS[0].id, kind: 'spec_sheet', path: 'mock/spec.pdf', name: 'AS350B3e-N350SR-spec.pdf', public: true, created_at: daysAgo(40) },
  { id: mkId(2, 'd'), listing_id: LISTINGS[0].id, kind: 'logbook', path: 'mock/logs.pdf', name: 'Airframe logs 2016-2026.pdf', public: false, created_at: daysAgo(40) },
];
export const PRICE_CHANGES: T.ScheduledPriceChange[] = [
  { id: mkId(1, 'p'), listing_id: LISTINGS[1].id, price_cents: 189_000_000, effective_at: daysAhead(5), applied_at: null },
];

// ── leads & messages ─────────────────────────────────────────────────────────
type LeadSeed = {
  n: number; listing: number | null; name: string; email: string | null; phone?: string | null;
  source: T.LeadSource; status: T.LeadStatus; assigned: string | null; ageH: number; follow?: number | null; notes?: string;
  msgs: [side: 'buyer' | 'seller' | 'system', body: string, hAgo: number, by?: string, flags?: string[]][];
  unread?: boolean;
};
const LEAD_SEEDS: LeadSeed[] = [
  { n: 1, listing: 1, name: 'Captain Ray Ellison', email: 'ray.ellison@alpinecharter.com', phone: '+1 970-555-0198', source: 'message', status: 'new', assigned: null, ageH: 1, unread: true,
    msgs: [['buyer', 'Good morning — is the B3e still available? We operate two B2s out of Aspen and are looking to step up. Can you send the last annual and the engine trend data?', 1]] },
  { n: 2, listing: 7, name: 'Heli-Tours Hawaii (Kai)', email: 'kai@helitourshi.com', source: 'web_form', status: 'new', assigned: U_MARCO, ageH: 5, unread: true,
    msgs: [['buyer', 'Interested in the H125 for our Kauai operation. What would it take to get it on a ship to Honolulu, and is the seller open to a 60-day close?', 5]] },
  { n: 3, listing: 2, name: 'J. Morales', email: null, source: 'ad_click', status: 'new', assigned: null, ageH: 20, unread: true,
    msgs: [['buyer', 'Is the hook certified for 1,200 lb? Need it for powerline work.', 20]] },
  { n: 4, listing: 3, name: 'Tessa Grant', email: 'tessa.g@gmail.com', source: 'call_tap', status: 'new', assigned: U_MARCO, ageH: 30,
    msgs: [['system', 'Tessa tapped “Call” on the listing from the LowOps app.', 30]] },
  { n: 5, listing: 1, name: 'Northwind Air Ambulance', email: 'fleet@northwindems.org', phone: '+1 406-555-0110', source: 'message', status: 'contacted', assigned: U_PRIYA, ageH: 50, follow: -0.2,
    notes: 'Board meets on the 15th. Wants EMS conversion quote from Metro.',
    msgs: [
      ['buyer', 'We are evaluating single-engine options for a rural base. Does the B3e have the dual-hydraulic mod?', 52],
      ['seller', 'Hi — yes, it has the dual hydraulic system (standard on the B3e). I’ve attached the spec sheet. Happy to set up a call with our DOM.', 50, U_PRIYA],
      ['buyer', 'Thanks. Could you hold it until our board meets on the 15th?', 26],
    ] },
  { n: 6, listing: 2, name: 'Ridgeline Utility Helicopters', email: 'ops@ridgelineuh.com', source: 'message', status: 'qualified', assigned: U_MARCO, ageH: 120, follow: 1,
    notes: 'Pre-buy booked w/ Precision Heli, Grand Junction. Financing via AirFinance — pre-approved.',
    msgs: [
      ['buyer', 'We would like to schedule a pre-buy inspection next week. Grand Junction works for us.', 124],
      ['seller', 'Great — we can ferry it to KGJT on Tuesday. Precision Heli has a slot Wednesday.', 120, U_MARCO],
      ['buyer', 'Confirmed. Sending the LOI today.', 98],
      ['seller', 'Received the LOI, thank you. Deposit instructions will come from our escrow agent, Aero-Title, by phone only.', 96, U_MARCO],
    ] },
  { n: 7, listing: 7, name: 'Desert Sky Tours', email: 'gm@desertskytours.com', source: 'message', status: 'qualified', assigned: U_PRIYA, ageH: 200, follow: 3,
    msgs: [
      ['buyer', 'What is the earliest delivery for the H125? We need it for high season.', 205],
      ['seller', 'It can be ready within 10 days of a signed purchase agreement.', 200, U_PRIYA],
    ] },
  { n: 8, listing: 9, name: 'Bluebird Aero Parts', email: 'buy@bluebirdaero.com', source: 'email_tap', status: 'contacted', assigned: U_MARCO, ageH: 76,
    msgs: [['buyer', 'Can you send the 8130 and the last shop findings report for the C20B?', 80], ['seller', 'Attached both. Happy to answer anything on the teardown.', 76, U_MARCO]] },
  { n: 9, listing: 10, name: 'Avionics Direct', email: null, source: 'feed_card', status: 'contacted', assigned: null, ageH: 140,
    msgs: [['buyer', 'Would you take $10,500 each for both GTNs?', 150], ['seller', 'We can do $11,800 each for the pair.', 140, U_ME]] },
  { n: 10, listing: 8, name: 'Copperline Aviation', email: 'chris@copperline.aero', source: 'message', status: 'won', assigned: U_PRIYA, ageH: 400,
    notes: 'Closed at $725k. Delivered at KBJC.',
    msgs: [['buyer', 'We will take it at $725,000.', 410], ['seller', 'Accepted — congratulations! Paperwork to follow.', 400, U_PRIYA]] },
  { n: 11, listing: 5, name: 'County Sheriff Air Unit', email: 'aviation@co-sheriff.gov', source: 'web_form', status: 'lost', assigned: U_MARCO, ageH: 600,
    notes: 'Went with a new R66 from Robinson factory.',
    msgs: [['buyer', 'Is the FLIR included in the price?', 610], ['seller', 'Yes — the Trakka and FLIR are included.', 600, U_MARCO]] },
  { n: 12, listing: 3, name: 'Paul Becker', email: 'pbecker@outlook.com', source: 'message', status: 'lost', assigned: null, ageH: 700,
    msgs: [['buyer', 'Too expensive for me, thanks anyway.', 700]] },
  { n: 13, listing: 1, name: 'Global Aircraft Logistics', email: 'agent.shipping.global@gmail.com', source: 'message', status: 'spam', assigned: null, ageH: 90,
    msgs: [['buyer', 'I am buying for my client overseas. I will pay by wire transfer to your account plus extra for my shipping agent, please contact on WhatsApp +44 7700 900123.', 90, undefined, ['scam_wire', 'off_platform']]] },
  { n: 14, listing: 6, name: 'Lucas Brandt', email: 'lucas@brandtfarms.com', source: 'message', status: 'contacted', assigned: U_MARCO, ageH: 30, follow: 0.1, unread: true,
    msgs: [
      ['buyer', 'Saw the 500E was paused — is it still for sale? I’m interested for ag work.', 34],
      ['seller', 'It is! We paused it while it’s in for a 100-hr. Back next week.', 32, U_MARCO],
      ['buyer', 'Perfect. Could I come see it Saturday?', 3],
    ] },
];

export const LEADS: T.Lead[] = [];
export const MESSAGES: T.LeadMessage[] = [];
for (const s of LEAD_SEEDS) {
  const id = mkId(s.n, 'e');
  const last = Math.min(...s.msgs.map((m) => m[2]));
  const firstSeller = s.msgs.filter((m) => m[0] === 'seller').map((m) => m[2]);
  LEADS.push({
    id, account_id: A_SUMMIT, listing_id: s.listing ? mkId(s.listing, 'l') : null, buyer_id: mkId(100 + s.n, 'u'),
    buyer_email: s.email, buyer_name: s.name, buyer_phone: s.phone ?? null, source: s.source,
    campaign_id: s.source === 'ad_click' ? mkId(1, 'k') : null, creative_id: null, status: s.status, assigned_to: s.assigned,
    notes: s.notes ?? null, follow_up_at: s.follow != null ? daysAhead(s.follow) : null,
    first_response_at: firstSeller.length ? daysAgo(0, Math.max(...firstSeller)) : null,
    last_activity_at: daysAgo(0, last), created_at: daysAgo(0, s.ageH), reply_token: `rt${s.n}x${id.slice(-6)}`,
  });
  s.msgs.forEach((m, i) => {
    const [side, body, hAgo, by, flags] = m;
    const isLast = i === s.msgs.length - 1;
    MESSAGES.push({
      id: mkId(s.n * 100 + i, 'g'), lead_id: id, sender_id: side === 'seller' ? (by ?? U_ME) : side === 'buyer' ? mkId(100 + s.n, 'u') : null,
      sender_side: side, sender_name: side === 'seller' ? USERS[by ?? U_ME]?.name : side === 'buyer' ? s.name : 'LowOps',
      body, attachments: side === 'seller' && /attached/i.test(body)
        ? [{ path: 'mock/spec.pdf', name: 'Spec sheet.pdf', mime: 'application/pdf', size: 482113 }] : [],
      via: s.source === 'web_form' && side === 'buyer' ? 'web' : side === 'system' ? 'system' : 'app',
      flags: flags ?? [], created_at: daysAgo(0, hAgo),
      read_by_buyer_at: side === 'seller' ? daysAgo(0, hAgo - 0.5) : null,
      read_by_seller_at: side === 'buyer' && s.unread && isLast ? null : daysAgo(0, hAgo - 0.2),
    });
  });
}

export const SAVED_REPLIES: T.SavedReply[] = [
  { id: mkId(1, 'r'), account_id: A_SUMMIT, title: 'Still available', body: 'Hi {name} — thanks for reaching out. Yes, the aircraft is still available. When would be a good time for a call?', created_by: U_ME },
  { id: mkId(2, 'r'), account_id: A_SUMMIT, title: 'Send logs', body: 'Happy to share the logs. I’ve attached the spec sheet; for the full logbook scans we ask for a signed NDA — I’ll send it over now.', created_by: U_PRIYA },
  { id: mkId(3, 'r'), account_id: A_SUMMIT, title: 'Pre-buy logistics', body: 'We can position the aircraft at KBJC or KGJT for a pre-buy. Buyer selects the shop; we cover ferry within Colorado.', created_by: U_MARCO },
  { id: mkId(4, 'r'), account_id: A_SUMMIT, title: 'Payment safety', body: 'For your protection we only accept funds through a recognised aircraft escrow/title company, confirmed by phone. We never change wiring instructions by email.', created_by: U_ME },
];

// ── policy, programs, grants ─────────────────────────────────────────────────
export const POLICIES: T.PricingPolicy[] = [
  {
    id: mkId(1, 'y'), effective_at: daysAgo(17), created_by: U_ME, note: 'Launch policy — Mode A (listing is the ad).',
    mode: 'listing_is_ad', individual_free_listings_per_year: 1, free_listing_days: 90, max_active_free_mode: 25,
    listing_fee_cents_per_day: { helicopter: 200, airplane: 200, gyroplane: 100, engine: 50, avionics: 25, part: 10, accessory: 10, service: 50, other: 10 },
    promoted_cpc_cents: 150, promoted_cpm_cents: 1200, brand_cpm_cents: 1500, min_daily_budget_cents: 500,
    tier_daily_caps_cents: { '0': 5000, '1': 25000, '2': 100000, '3': 1000000 }, grandfather_existing: true,
    frequency_cap_per_day: 3, min_audience: 100, invalid_click_window_minutes: 30,
  },
];

export const PROGRAMS: T.CreditProgram[] = [
  { id: mkId(1, 'q'), name: 'Launch Partner — $2,000', code: null, amount_cents: 200000, expires_after_days: 90, applies_to: ['listing_fee', 'promotion', 'brand'], requires_card: true, max_redemptions: null, per_account_limit: 1, active: true, created_by: U_ME, created_at: daysAgo(50), redemptions: 2 },
  { id: mkId(2, 'q'), name: 'HAI HELI-EXPO 2027', code: 'HELIEXPO27', amount_cents: 25000, expires_after_days: 30, applies_to: ['promotion', 'brand'], requires_card: true, max_redemptions: 500, per_account_limit: 1, active: true, created_by: U_ME, created_at: daysAgo(10), redemptions: 37 },
  { id: mkId(3, 'q'), name: 'Welcome credit (retired)', code: 'WELCOME50', amount_cents: 5000, expires_after_days: 60, applies_to: ['listing_fee'], requires_card: true, max_redemptions: null, per_account_limit: 1, active: false, created_by: U_ME, created_at: daysAgo(48), redemptions: 12 },
];

export const G_LAUNCH = mkId(1, 'h');
export const G_EXPO = mkId(2, 'h');
export const G_RT = mkId(3, 'h');

// ── campaigns ────────────────────────────────────────────────────────────────
export const C_PROMO = mkId(1, 'k');
export const C_BRAND = mkId(2, 'k');

const ALL_PLACEMENTS: T.Placement[] = ['feed', 'search_top', 'listing_similar', 'web_search', 'web_listing'];

export const CAMPAIGNS: T.Campaign[] = [
  {
    id: C_PROMO, account_id: A_SUMMIT, name: 'Turbine singles — Mountain West', kind: 'promotion', objective: 'leads', status: 'active',
    pause_reason: null, daily_budget_cents: 4000, lifetime_budget_cents: null, starts_at: daysAgo(21), ends_at: daysAhead(40),
    pricing: { model: 'cpc', rate_cents: 150 },
    targeting: { type_ratings: ['AS350', 'B407', 'EC130'], hours_bands: ['1500-5000', '5000+'], states: ['CO', 'UT', 'WY', 'ID', 'MT', 'NM'], segments: ['tour', 'utility', 'hems'] },
    placements: ALL_PLACEMENTS, spent_today_cents: 2210, spent_total_cents: 0, spend_day: dayStr(0), created_by: U_ME, created_at: daysAgo(22), updated_at: daysAgo(3),
    creatives: [1, 2, 7].map((n, i) => ({
      id: mkId(10 + i, 'v'), campaign_id: C_PROMO, kind: 'listing' as const, listing_id: mkId(n, 'l'), headline: null, body: null, image_url: null,
      target_url: null, cta_label: null, review_status: 'approved' as const, review_reasons: [],
    })),
  },
  {
    id: C_BRAND, account_id: A_SUMMIT, name: 'Summit brand — fall awareness', kind: 'brand', objective: 'awareness', status: 'paused',
    pause_reason: 'Paused by Dana Whitaker', daily_budget_cents: 2500, lifetime_budget_cents: 60000, starts_at: daysAgo(30), ends_at: null,
    pricing: { model: 'cpm', rate_cents: 1500 },
    targeting: { context: { kinds: ['helicopter'] }, states: ['CO', 'UT', 'AZ', 'NM'] },
    placements: ['feed', 'web_search', 'web_listing'], spent_today_cents: 0, spent_total_cents: 0, spend_day: dayStr(4), created_by: U_PRIYA, created_at: daysAgo(31), updated_at: daysAgo(4),
    creatives: [{
      id: mkId(20, 'v'), campaign_id: C_BRAND, kind: 'brand', listing_id: null, headline: 'Turbine helicopters, honestly inspected',
      body: 'Every Summit aircraft ships with our own pre-buy findings. Front Range & Western Slope.', image_url: photo(3, 'Summit Rotor Sales'),
      target_url: 'https://summitrotor.com/inventory', cta_label: 'See inventory', review_status: 'approved', review_reasons: [],
    }],
  },
  // listing-fee campaigns (Mode A presence fee), one per paid active/paused listing
  ...[1, 2, 3, 5, 6, 7, 9, 10].map((n): T.Campaign => {
    const l = LISTINGS[n - 1];
    const rate = l.kind === 'engine' ? 50 : l.kind === 'avionics' ? 25 : 200;
    return {
      id: mkId(100 + n, 'k'), account_id: A_SUMMIT, name: `Listing fee · ${l.title.split(' — ')[0]}`, kind: 'listing', objective: 'presence',
      status: l.state === 'paused' ? 'paused' : 'active', pause_reason: l.state === 'paused' ? 'Listing paused' : null,
      daily_budget_cents: rate, lifetime_budget_cents: null, starts_at: l.published_at, ends_at: null,
      pricing: { model: 'per_day', rate_cents: rate }, targeting: {}, placements: ['feed', 'search_top'],
      spent_today_cents: l.state === 'paused' ? 0 : rate, spent_total_cents: 0, spend_day: dayStr(0), created_by: null,
      created_at: l.published_at ?? daysAgo(30), updated_at: daysAgo(1),
      creatives: [{ id: mkId(200 + n, 'v'), campaign_id: mkId(100 + n, 'k'), kind: 'listing', listing_id: l.id, headline: null, body: null, image_url: null, target_url: null, cta_label: null, review_status: l.review_status, review_reasons: [] }],
    };
  }),
];

/** Per-campaign per-day stats for the last 60 days. */
export const STATS: (T.StatPoint & { campaign_id: string })[] = [];
{
  const rnd = prng(42);
  for (let d = 59; d >= 0; d--) {
    const day = dayStr(d);
    // promotion: started 21 days ago
    if (d <= 21) {
      const imp = Math.round(1400 + rnd() * 900 + (21 - d) * 18);
      const clicks = Math.round(imp * (0.012 + rnd() * 0.01));
      const cost = Math.min(4000, clicks * 150);
      STATS.push({ campaign_id: C_PROMO, day, impressions: imp, clicks, leads: rnd() < 0.18 ? 1 : 0, cost_cents: d === 0 ? 2210 : cost });
    }
    // brand: ran days 30..4
    if (d <= 30 && d >= 4) {
      const imp = Math.round(520 + rnd() * 180);
      STATS.push({ campaign_id: C_BRAND, day, impressions: imp, clicks: Math.round(imp * 0.004 + rnd() * 3), leads: 0, cost_cents: Math.round((imp * 1500) / 1000) });
    }
    for (const c of CAMPAIGNS.filter((x) => x.kind === 'listing')) {
      const started = c.starts_at ? (NOW - Date.parse(c.starts_at)) / DAY : 30;
      if (d > started) continue;
      if (c.status === 'paused' && d < 9) continue;
      const imp = Math.round(300 + rnd() * 260);
      STATS.push({ campaign_id: c.id, day, impressions: imp, clicks: Math.round(imp * (0.03 + rnd() * 0.03)), leads: rnd() < 0.07 ? 1 : 0, cost_cents: c.pricing.rate_cents });
    }
  }
  for (const c of CAMPAIGNS) {
    c.spent_total_cents = STATS.filter((s) => s.campaign_id === c.id).reduce((a, s) => a + s.cost_cents, 0);
  }
}

/** Organic listing views per day (account-wide), for dashboard/analytics. */
export const ORGANIC: { day: string; views: number; saves: number }[] = [];
{
  const rnd = prng(7);
  for (let d = 59; d >= 0; d--) {
    const weekend = [0, 6].includes(new Date(NOW - d * DAY).getUTCDay());
    ORGANIC.push({ day: dayStr(d), views: Math.round(170 + rnd() * 90 + (59 - d) * 2.2 + (weekend ? 60 : 0)), saves: Math.round(3 + rnd() * 6) });
  }
}

// ── ledger ───────────────────────────────────────────────────────────────────
export const LEDGER: T.LedgerRow[] = [];
export const GRANTS: T.CreditGrant[] = [];
{
  let n = 0;
  const push = (r: Omit<T.LedgerRow, 'id' | 'account_id'> & { account_id?: string }) =>
    LEDGER.push({ id: ++n, account_id: r.account_id ?? A_SUMMIT, ...r } as T.LedgerRow);
  const base = { purpose: null, grant_id: null, campaign_id: null, listing_id: null, stripe_ref: null, memo: null };

  // paid era: topup and a few days of listing fees before the Launch Partner grant
  push({ ...base, occurred_at: daysAgo(58), entry_type: 'topup', funding: 'paid', amount_cents: 50000, stripe_ref: 'pi_mock_3Pq1', memo: 'Top-up' });
  for (let d = 57; d > 44; d--) {
    push({ ...base, occurred_at: daysAgo(d, -1), entry_type: 'spend', funding: 'paid', amount_cents: -600, purpose: 'listing_fee', memo: 'Listing fees (3 listings)' });
  }
  push({ ...base, occurred_at: daysAgo(30), entry_type: 'invalid_traffic_credit', funding: 'paid', amount_cents: 450, memo: 'Invalid clicks credited (3)' });

  const launch: T.CreditGrant = {
    id: G_LAUNCH, program_id: PROGRAMS[0].id, program_name: PROGRAMS[0].name, account_id: A_SUMMIT, account_name: 'Summit Rotor Sales',
    amount_cents: 200000, remaining_cents: 200000, applies_to: ['listing_fee', 'promotion', 'brand'], expires_at: daysAhead(47),
    granted_at: daysAgo(43), granted_by: U_ME, note: 'Launch partner — signed at HAI regional',
  };
  const expo: T.CreditGrant = {
    id: G_EXPO, program_id: PROGRAMS[1].id, program_name: PROGRAMS[1].name, account_id: A_SUMMIT, account_name: 'Summit Rotor Sales',
    amount_cents: 25000, remaining_cents: 25000, applies_to: ['promotion', 'brand'], expires_at: daysAhead(9),
    granted_at: daysAgo(21), granted_by: null, note: 'Redeemed code HELIEXPO27',
  };
  push({ ...base, occurred_at: launch.granted_at, entry_type: 'credit_grant', funding: 'promo', amount_cents: 200000, grant_id: G_LAUNCH, memo: launch.program_name ?? null });
  push({ ...base, occurred_at: expo.granted_at, entry_type: 'credit_grant', funding: 'promo', amount_cents: 25000, grant_id: G_EXPO, memo: 'Code HELIEXPO27' });

  // promo era: daily listing fees + campaign spend, soonest-expiring applicable grant first
  const byDay = new Map<string, Map<string, number>>();
  for (const s of STATS) {
    const dd = Math.round((NOW - Date.parse(`${s.day}T12:00:00Z`)) / DAY);
    if (dd > 43) continue;
    const m = byDay.get(s.day) ?? new Map<string, number>();
    m.set(s.campaign_id, (m.get(s.campaign_id) ?? 0) + s.cost_cents);
    byDay.set(s.day, m);
  }
  for (const [day, m] of [...byDay.entries()].sort()) {
    let fee = 0;
    for (const [cid, cents] of m) {
      const c = CAMPAIGNS.find((x) => x.id === cid)!;
      if (c.kind === 'listing') { fee += cents; continue; }
      const purpose: T.SpendPurpose = c.kind === 'brand' ? 'brand' : 'promotion';
      let left = cents;
      for (const g of [expo, launch]) {
        if (left <= 0 || !g.applies_to.includes(purpose) || g.remaining_cents <= 0 || Date.parse(day) < Date.parse(g.granted_at.slice(0, 10))) continue;
        const take = Math.min(left, g.remaining_cents);
        g.remaining_cents -= take; left -= take;
        push({ ...base, occurred_at: `${day}T23:10:00.000Z`, entry_type: 'spend', funding: 'promo', amount_cents: -take, purpose, grant_id: g.id, campaign_id: cid, memo: c.name });
      }
      if (left > 0) push({ ...base, occurred_at: `${day}T23:10:00.000Z`, entry_type: 'spend', funding: 'paid', amount_cents: -left, purpose, campaign_id: cid, memo: c.name });
    }
    if (fee > 0) {
      launch.remaining_cents -= fee;
      push({ ...base, occurred_at: `${day}T00:05:00.000Z`, entry_type: 'spend', funding: 'promo', amount_cents: -fee, purpose: 'listing_fee', grant_id: G_LAUNCH, memo: 'Daily listing fees' });
    }
  }
  LEDGER.sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at));
  LEDGER.forEach((r, i) => (r.id = i + 1));
  GRANTS.push(launch, expo, {
    id: G_RT, program_id: PROGRAMS[0].id, program_name: PROGRAMS[0].name, account_id: A_RT, account_name: 'RotorTrade',
    amount_cents: 200000, remaining_cents: 164320, applies_to: ['listing_fee', 'promotion', 'brand'], expires_at: daysAhead(66),
    granted_at: daysAgo(24), granted_by: U_ME, note: 'RotorTrade launch partnership',
  });
}

// ── feeds, imports, keys, hooks ──────────────────────────────────────────────
export const FEEDS: T.Feed[] = [
  {
    id: mkId(1, 'f'), account_id: A_SUMMIT, name: 'summitrotor.com inventory', url: 'https://summitrotor.com/inventory/feed.xml', format: 'xml',
    mapping: { StockNo: 'external_id', Year: 'year', Make: 'make', Model: 'model', TTAF: 'airframe_total_hours', Price: 'price' },
    schedule: 'daily', active: true, last_run_at: daysAgo(0, 7), last_status: 'ok', last_error: null, created_at: daysAgo(40),
  },
];
export const IMPORT_RUNS: T.ImportRun[] = [
  { id: mkId(1, 'i'), account_id: A_SUMMIT, feed_id: FEEDS[0].id, source: 'feed', started_at: daysAgo(0, 7), finished_at: daysAgo(0, 7), created: 0, updated: 1, unchanged: 9, removed: 0, failed: 0, errors: [] },
  { id: mkId(2, 'i'), account_id: A_SUMMIT, feed_id: FEEDS[0].id, source: 'feed', started_at: daysAgo(1, 7), finished_at: daysAgo(1, 7), created: 1, updated: 0, unchanged: 9, removed: 0, failed: 1, errors: [{ row: 11, field: 'Price', message: '“Call” is not a number — set price on request instead' }] },
  { id: mkId(3, 'i'), account_id: A_SUMMIT, feed_id: null, source: 'csv_upload', started_at: daysAgo(36), finished_at: daysAgo(36), created: 8, updated: 0, unchanged: 0, removed: 0, failed: 0, errors: [] },
];
export const API_KEYS: T.ApiKey[] = [
  { id: mkId(1, 'x'), account_id: A_SUMMIT, name: 'Website inventory sync', last4: 'a91f', created_at: daysAgo(39), revoked_at: null },
  { id: mkId(2, 'x'), account_id: A_SUMMIT, name: 'Old DMS', last4: '07bc', created_at: daysAgo(58), revoked_at: daysAgo(39) },
];
export const WEBHOOKS: T.Webhook[] = [
  { id: mkId(1, 'w'), account_id: A_SUMMIT, url: 'https://hooks.summitrotor.com/lowops', events: ['lead.created', 'listing.state_changed'], active: true, created_at: daysAgo(30) },
];

// ── staff data ───────────────────────────────────────────────────────────────
export const REVIEW_RUNS: T.ReviewRun[] = [
  { id: 9, target_type: 'listing', target_id: LISTINGS[3].id, target_label: LISTINGS[3].title, account_name: 'Summit Rotor Sales', checks: { attributes: 'ok', registry: 'pending', photos: 'ok', model: 'queued' }, decision: 'pending', model: null, occurred_at: daysAgo(0, 2) },
  { id: 8, target_type: 'listing', target_id: LISTINGS[11].id, target_label: LISTINGS[11].title, account_name: 'Summit Rotor Sales', checks: { photo_dhash: { collision_with: 'Pacific Heli Brokers', hamming: 3 }, registry: 'not_found', scam_risk: 0.12 }, decision: 'rejected', model: 'claude-haiku-4-5', occurred_at: daysAgo(3) },
  { id: 7, target_type: 'listing', target_id: LISTINGS[4].id, target_label: LISTINGS[4].title, account_name: 'Summit Rotor Sales', checks: { registry: 'mismatch', registry_serial: '0515', scam_risk: 0.04 }, decision: 'limited', model: 'claude-haiku-4-5', occurred_at: daysAgo(9) },
  { id: 6, target_type: 'creative', target_id: mkId(20, 'v'), target_label: 'Turbine helicopters, honestly inspected', account_name: 'Summit Rotor Sales', checks: { text: 'ok', image: 'ok', url: 'summitrotor.com ok' }, decision: 'approved', model: 'claude-haiku-4-5', occurred_at: daysAgo(31) },
  { id: 5, target_type: 'listing', target_id: mkId(301, 'l'), target_label: '2017 Bell 505 — like new', account_name: 'Pacific Heli Brokers', checks: { price_outlier: { median: 120000000, price: 19900000 }, scam_risk: 0.81 }, decision: 'rejected', model: 'claude-haiku-4-5', occurred_at: daysAgo(14) },
  { id: 4, target_type: 'account', target_id: A_PACIFIC, target_label: 'Pacific Heli Brokers', account_name: 'Pacific Heli Brokers', checks: { reports: 4, card_fingerprint_reuse: true }, decision: 'rejected', model: null, occurred_at: daysAgo(13) },
  { id: 3, target_type: 'listing', target_id: LISTINGS[0].id, target_label: LISTINGS[0].title, account_name: 'Summit Rotor Sales', checks: { registry: 'matched', photos: 'ok', scam_risk: 0.01, mappings: 1 }, decision: 'approved', model: 'claude-haiku-4-5', occurred_at: daysAgo(42) },
  { id: 2, target_type: 'listing', target_id: mkId(401, 'l'), target_label: '2015 R44 Raven I', account_name: 'Mike Torres', checks: { registry: 'matched', model: 'unreachable' }, decision: 'limited', model: null, occurred_at: daysAgo(11) },
  { id: 1, target_type: 'listing', target_id: mkId(501, 'l'), target_label: 'Bell 206 tail rotor blades (pair)', account_name: 'High Desert Aviation', checks: { attributes: 'ok', scam_risk: 0.02 }, decision: 'approved', model: 'claude-haiku-4-5', occurred_at: daysAgo(18) },
];

export const AUDIT: T.AuditEntry[] = [
  { id: 12, actor_id: U_ME, actor_name: 'Dana Whitaker', actor_kind: 'staff', action: 'policy.create', target_type: 'pricing_policy', target_id: POLICIES[0].id, detail: { mode: 'listing_is_ad', effective_at: POLICIES[0].effective_at }, occurred_at: daysAgo(17) },
  { id: 11, actor_id: U_ME, actor_name: 'Dana Whitaker', actor_kind: 'staff', action: 'credit.grant', target_type: 'account', target_id: A_RT, detail: { amount_cents: 200000, program: 'Launch Partner — $2,000', expires_days: 90 }, occurred_at: daysAgo(24) },
  { id: 10, actor_id: U_ME, actor_name: 'Dana Whitaker', actor_kind: 'staff', action: 'credit.grant', target_type: 'account', target_id: A_SUMMIT, detail: { amount_cents: 200000, program: 'Launch Partner — $2,000', expires_days: 90 }, occurred_at: daysAgo(43) },
  { id: 9, actor_id: null, actor_name: 'system', actor_kind: 'system', action: 'account.suspend', target_type: 'account', target_id: A_PACIFIC, detail: { reason: 'card fingerprint reused across 3 accounts; 4 user reports' }, occurred_at: daysAgo(13) },
  { id: 8, actor_id: U_ME, actor_name: 'Dana Whitaker', actor_kind: 'staff', action: 'program.create', target_type: 'credit_program', target_id: PROGRAMS[1].id, detail: { code: 'HELIEXPO27', amount_cents: 25000 }, occurred_at: daysAgo(10) },
  { id: 7, actor_id: U_ME, actor_name: 'Dana Whitaker', actor_kind: 'staff', action: 'account.set', target_type: 'account', target_id: A_HIGHDESERT, detail: { trust_tier: [0, 1] }, occurred_at: daysAgo(8) },
  { id: 6, actor_id: null, actor_name: 'mkt-review', actor_kind: 'system', action: 'review.decision', target_type: 'listing', target_id: LISTINGS[11].id, detail: { decision: 'rejected', reasons: ['photo_reused', 'registry_not_found'] }, occurred_at: daysAgo(3) },
  { id: 5, actor_id: null, actor_name: 'mkt-billing-cron', actor_kind: 'system', action: 'charge.auto_recharge', target_type: 'account', target_id: A_RT, detail: { amount_cents: 25000, status: 'succeeded' }, occurred_at: daysAgo(2) },
];
